import { useState } from 'react';
import { parseTsv, detectStructure, transpose } from '../../utils/sprintImport';
import type { DetectionResult, SprintInfo } from '../../utils/sprintImport';
import DetectionPreview from './DetectionPreview';

interface PasteImportModalProps {
  sprints: SprintInfo[];
  existingMetricsCount: number;
  onImport: (result: DetectionResult) => void;
  onBulkEdit: (result: DetectionResult) => void;
  onClose: () => void;
}

export default function PasteImportModal({
  sprints,
  existingMetricsCount,
  onImport,
  onBulkEdit,
  onClose,
}: PasteImportModalProps) {
  const [text, setText] = useState('');
  const [result, setResult] = useState<DetectionResult | null>(null);
  const [originalData, setOriginalData] = useState<string[][] | null>(null);
  const [parsing, setParsing] = useState(false);

  const handleParse = () => {
    if (!text.trim()) return;
    setParsing(true);
    try {
      const data = parseTsv(text);
      if (data.length === 0 || (data.length === 1 && data[0].every((c) => !c.trim()))) {
        setParsing(false);
        return;
      }
      setOriginalData(data);
      const detected = detectStructure(data, sprints);
      setResult(detected);
    } finally {
      setParsing(false);
    }
  };

  const handleSwapOrientation = () => {
    if (!originalData) return;
    const swapped = transpose(originalData);
    const detected = detectStructure(swapped, sprints);
    detected.orientation = result?.orientation === 'normal' ? 'transposed' : 'normal';
    detected.transposed = detected.orientation === 'transposed';
    setResult(detected);
  };

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 overflow-y-auto">
      <div className="card max-w-2xl w-full mx-4 my-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Paste Sprint Metrics</h2>
          <button onClick={onClose} className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 rounded">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {result ? (
          <DetectionPreview
            result={result}
            sprints={sprints}
            existingMetricsCount={existingMetricsCount}
            onImport={onImport}
            onBulkEdit={onBulkEdit}
            onDiscard={onClose}
            onSwapOrientation={handleSwapOrientation}
          />
        ) : (
          <>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              Paste cells copied from Excel or Google Sheets:
            </p>
            <textarea
              className="input font-mono text-xs"
              rows={8}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Select cells in your spreadsheet, Ctrl+C, then Ctrl+V here..."
            />
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
              Tip: Copy a range of cells from your spreadsheet and paste here. Tab-separated values work best.
            </p>

            <div className="flex justify-end gap-3 mt-4">
              <button onClick={onClose} className="btn-outline text-sm">Cancel</button>
              <button
                onClick={handleParse}
                className="btn-primary text-sm"
                disabled={!text.trim() || parsing}
              >
                {parsing ? 'Parsing...' : 'Parse Data'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
